import React, { useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiWifi, FiWifiOff } from "react-icons/fi";
import { MdOutlineWaterDrop } from "react-icons/md";
import { TankBar, MiniBar, levelColor, levelLabel } from "./waterHelpers";

const TYPE_COLOR = { Ground: "#3b82f6", Overhead: "#06b6d4", Reserve: "#10b981", Treated: "#8b5cf6" };

const WmTankPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const sel = selected ? d.tanks.find(t => t.id === selected) : null;

  return (
    <div className="wm-tanks">

      {/* ── Summary strip ── */}
      <div className="wt-summary-row">
        <div className="wt-sum-chip" style={{ background: "#fee2e2" }}>
          <FiAlertTriangle size={14} color="#ef4444" />
          <span style={{ color: "#ef4444" }}>
            {d.tanks.filter(t => t.overflowRisk || t.dryRunRisk).length} Risk Tanks
          </span>
        </div>
        <div className="wt-sum-chip" style={{ background: "#d1fae5" }}>
          <FiCheckCircle size={14} color="#10b981" />
          <span style={{ color: "#10b981" }}>
            {d.tanks.filter(t => t.status === "Online" && !t.overflowRisk && !t.dryRunRisk).length} Normal
          </span>
        </div>
        <div className="wt-sum-chip" style={{ background: "#dbeafe" }}>
          <MdOutlineWaterDrop size={14} color="#3b82f6" />
          <span style={{ color: "#3b82f6" }}>
            Total: {(d.tanks.reduce((acc, t) => acc + t.currentLevel, 0) / 1000).toFixed(0)} KL / {(d.tanks.reduce((acc, t) => acc + t.capacity, 0) / 1000).toFixed(0)} KL
          </span>
        </div>
      </div>

      <div className="wt-layout">

        {/* ── Tank Cards Grid ── */}
        <div className="wt-grid">
          {d.tanks.map(tank => {
            const col = levelColor(tank.levelPct);
            const typeCol = TYPE_COLOR[tank.type] || "#667eea";
            const isSelected = selected === tank.id;
            return (
              <div
                key={tank.id}
                className={`wt-card ${isSelected ? "wt-card-active" : ""} ${tank.overflowRisk ? "wt-card-overflow" : ""} ${tank.dryRunRisk ? "wt-card-dryrun" : ""}`}
                onClick={() => setSelected(isSelected ? null : tank.id)}
              >
                {/* Risk badges */}
                {tank.overflowRisk && <div className="wt-risk-badge wt-overflow">⚠ Overflow Risk</div>}
                {tank.dryRunRisk   && <div className="wt-risk-badge wt-dryrun">⚠ Dry Run Risk</div>}

                <div className="wt-card-header">
                  <div>
                    <div className="wt-card-name">{tank.name}</div>
                    <div className="wt-card-location">{tank.location}</div>
                    <span className="wt-type-badge" style={{ background: `${typeCol}18`, color: typeCol }}>{tank.type}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <TankBar pct={tank.levelPct} height={70} width={32} />
                    {tank.status === "Online"
                      ? <FiWifi size={12} color="#10b981" />
                      : <FiWifiOff size={12} color="#ef4444" />}
                  </div>
                </div>

                <div className="wt-stats-grid">
                  <div className="wt-stat">
                    <span className="wt-stat-label">Level</span>
                    <span className="wt-stat-val" style={{ color: col }}>{tank.levelPct}% · {(tank.currentLevel / 1000).toFixed(1)} KL</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">Status</span>
                    <span className="wt-stat-val" style={{ color: col }}>{levelLabel(tank.levelPct)}</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">Inflow</span>
                    <span className="wt-stat-val">{tank.inflow} L/min</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">Outflow</span>
                    <span className="wt-stat-val">{tank.outflow} L/min</span>
                  </div>
                </div>
                <MiniBar value={tank.levelPct} color={col} height={5} />
              </div>
            );
          })}
        </div>

        {/* ── Detail Panel ── */}
        <div className="wt-detail-panel">
          {sel ? (
            <>
              <div className="wt-detail-title">{sel.name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", margin: "1rem 0" }}>
                <TankBar pct={sel.levelPct} height={140} width={56} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "2rem", fontWeight: 800, color: levelColor(sel.levelPct) }}>{sel.levelPct}%</div>
                  <div style={{ fontSize: "0.82rem", color: "#64748b" }}>{(sel.currentLevel / 1000).toFixed(2)} KL of {(sel.capacity / 1000).toFixed(0)} KL</div>
                  <div style={{ marginTop: 8 }}>
                    {sel.overflowRisk && <div className="wt-risk-badge wt-overflow" style={{ position: "static", marginBottom: 4 }}>⚠ Overflow Risk – Stop Inflow</div>}
                    {sel.dryRunRisk   && <div className="wt-risk-badge wt-dryrun"   style={{ position: "static", marginBottom: 4 }}>⚠ Dry Run Risk – Start Pump</div>}
                  </div>
                </div>
              </div>

              <div className="wt-detail-grid">
                {[
                  ["Type",         sel.type],
                  ["Location",     sel.location],
                  ["Capacity",     `${(sel.capacity/1000).toFixed(0)} KL`],
                  ["Inflow",       `${sel.inflow} L/min`],
                  ["Outflow",      `${sel.outflow} L/min`],
                  ["Refill ETA",   sel.refillEta],
                  ["Last Refilled",sel.lastRefilled],
                  ["Source",       sel.suppliedFrom],
                  ["Comm Status",  sel.status],
                ].map(([label, val]) => (
                  <div key={label} className="wt-dg-item">
                    <span className="wt-dg-label">{label}</span>
                    <span className="wt-dg-val">{val}</span>
                  </div>
                ))}
              </div>

              {/* Flow balance */}
              {sel.inflow > 0 && sel.outflow > 0 && (
                <div className="wt-flow-balance">
                  <div className="wt-fb-label">Net Flow</div>
                  <div className="wt-fb-val" style={{ color: sel.inflow > sel.outflow ? "#10b981" : "#ef4444" }}>
                    {sel.inflow > sel.outflow ? "+" : ""}{sel.inflow - sel.outflow} L/min
                    &nbsp;({sel.inflow > sel.outflow ? "Filling" : "Draining"})
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="wt-empty">
              <MdOutlineWaterDrop size={44} color="#cbd5e1" />
              <p>Select a tank to view detailed status</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Multi-tank Comparison Bar ── */}
      <div className="wt-comparison-card">
        <div className="wt-comp-title">Multi-Tank Level Comparison</div>
        <div className="wt-comp-bars">
          {d.tanks.map(tank => (
            <div key={tank.id} className="wt-comp-col">
              <div className="wt-comp-bar-wrap">
                <div className="wt-comp-bar" style={{ height: `${tank.levelPct}%`, background: levelColor(tank.levelPct) }} />
              </div>
              <div className="wt-comp-label">{tank.id}</div>
              <div className="wt-comp-pct" style={{ color: levelColor(tank.levelPct) }}>{tank.levelPct}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WmTankPanel;
